import React, { useMemo } from "react";

/**
 * Plain-SVG line + area chart for the responses-per-day series.
 * series = [{date: "YYYY-MM-DD", count: int}]
 */
export function LineChart({ series, title }) {
    const W = 720;
    const H = 220;
    const PADX = 40;
    const PADY = 20;

    const { path, area, ticks, dots, max } = useMemo(() => {
        if (!series || series.length === 0) {
            return { path: "", area: "", ticks: [], dots: [], max: 0 };
        }
        const maxV = Math.max(1, ...series.map((s) => s.count));
        const stepX = (W - PADX * 2) / Math.max(1, series.length - 1);
        const points = series.map((s, i) => {
            const x = PADX + i * stepX;
            const y = PADY + (1 - s.count / maxV) * (H - PADY * 2);
            return [x, y, s];
        });
        const linePath = points
            .map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)} ${y.toFixed(1)}`)
            .join(" ");
        const areaPath = `${linePath} L${(PADX + (series.length - 1) * stepX).toFixed(1)} ${(H - PADY).toFixed(1)} L${PADX} ${(H - PADY).toFixed(1)} Z`;

        const tickIndexes = [0, Math.floor(series.length / 2), series.length - 1];
        const tk = tickIndexes.map((i) => ({
            x: PADX + i * stepX,
            label: series[i]?.date?.slice(5) || "",
        }));
        return {
            path: linePath,
            area: areaPath,
            ticks: tk,
            dots: points,
            max: maxV,
        };
    }, [series]);

    return (
        <div
            className="rounded-2xl border border-klareet-bor bg-klareet-card p-6"
            data-testid="line-chart"
        >
            {title ? (
                <div className="flex items-center justify-between mb-3">
                    <div className="text-[11px] uppercase tracking-[0.2em] font-mono text-klareet-MUT">
                        {title}
                    </div>
                    <div className="text-[11px] font-mono text-klareet-DIM">
                        peak {max}
                    </div>
                </div>
            ) : null}
            <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto">
                <defs>
                    <linearGradient id="kl-area" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#14B8A6" stopOpacity="0.35" />
                        <stop offset="100%" stopColor="#14B8A6" stopOpacity="0" />
                    </linearGradient>
                </defs>
                {[0, 1, 2, 3].map((i) => {
                    const y = PADY + ((H - PADY * 2) * i) / 3;
                    return (
                        <line
                            key={i}
                            x1={PADX}
                            x2={W - PADX}
                            y1={y}
                            y2={y}
                            stroke="#1A2540"
                            strokeWidth="1"
                        />
                    );
                })}
                {area ? <path d={area} fill="url(#kl-area)" /> : null}
                {path ? (
                    <path
                        d={path}
                        fill="none"
                        stroke="#14B8A6"
                        strokeWidth="2.5"
                        strokeLinejoin="round"
                        strokeLinecap="round"
                    />
                ) : null}
                {dots.map(([x, y, s]) => (
                    <g key={s.date}>
                        <circle
                            cx={x}
                            cy={y}
                            r={3}
                            fill="#0E1628"
                            stroke="#14B8A6"
                            strokeWidth="2"
                        />
                    </g>
                ))}
                {ticks.map((t) => (
                    <text
                        key={t.x}
                        x={t.x}
                        y={H - 4}
                        fontSize="10"
                        fontFamily="JetBrains Mono, monospace"
                        textAnchor="middle"
                        fill="#6B7FA3"
                    >
                        {t.label}
                    </text>
                ))}
            </svg>
        </div>
    );
}
